import { UserBillingStatus } from "~/graphql/codegen/graphql";
import { useUser } from "~/hooks/context-hook";

/**
 * Whether the learner is entitled to the Plus-gated learner surfaces.
 *
 *   access = active Plus billing  OR  the learner is on the self-serve policy
 *
 * The second term exists because a self-serve learner has **no billing account
 * at all**, by design. `server/domains/billing/events.ts::handleUserJoinedCohort`
 * early-returns for a `SelfServed` cohort so the sponsored account is never
 * minted, and `server/edge/graphql/resolvers/billing.ts` then returns
 * `billing: null` for them and skips both the licence-date grant and the
 * force-`ACTIVE` fallback. So `billing.status === ACTIVE` is false forever for
 * a self-serve learner, and any lock keyed on it alone closes over the program
 * they bought — the "Renew to unlock" card on /programs and the
 * "Upgrade to unlock" use-case cards after the personalizer.
 *
 * `selfServePolicy` is the server-derived flag already used to switch other
 * self-serve behaviour (see `components/hubspot-initializer.tsx`). It is true
 * only when EVERY cohort the learner is enrolled in is `SelfServed`
 * (`server/domains/mindspace-program/self-serve-policy.ts::resolveSelfServePolicy`),
 * so it cannot widen access for a Live or lapsed-Plus learner — this hook
 * leaves their locks exactly as they are today.
 *
 * HOW TO ADOPT THIS HOOK. It is the self-serve TERM, not a global "has access"
 * verdict. Where a call site's existing condition is plain
 * `billing.status === ACTIVE`, this hook replaces it outright. Where the site
 * carries EXTRA unlock terms — current-space enrollment
 * (`components/access-guards/access-guard.tsx`, `_app.use-cases/*`,
 * `_app.sandbox/*`, `_app.content/*`), a referral code
 * (`_app.profile.$username/*`), or a route exemption — those terms MUST be
 * preserved verbatim and this hook ORed on as one more alternative. Swapping
 * such a site to the hook alone would DROP its extra term and re-lock learners
 * who are unlocked today (an enrolled Live learner whose Plus subscription has
 * lapsed is exactly that case). Every adoption is additive; no site may be
 * narrowed.
 *
 * NB the all-or-nothing shape of that flag: a self-serve learner who also joins
 * a single Live cohort resolves to `false` and re-locks. If pilot learners ever
 * hold mixed enrolments, switch this to the per-cohort
 * `CohortMetadata.type === 'SelfServed'` (already exposed on the learner
 * schema — no server change needed).
 */
export function usePlatformAccess(): boolean {
  const user = useUser();

  return (
    user?.billing?.status === UserBillingStatus.Active
    || user?.selfServePolicy === true
  );
}
